import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Header from "components/Header";
import { toast } from "react-toastify";
import { Box, Dialog, DialogTitle, DialogActions, InputBase } from "@mui/material";
import { getAllProperties, deleteProperty, getAllOwners } from "state/apis";
import Add from "./Add";
import TitlebarImageList from "./Document";
import SearchIcon from "@mui/icons-material/Search";
import GetAppIcon from "@mui/icons-material/GetApp";
import { GiWallet } from "react-icons/gi";
import PropertyExpense from "./Expense";
import { useDispatch } from "react-redux";
import { getDefaultProperty } from "state";

const Properties = () => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [totalData, setTotalData] = React.useState([]);
    const [openAddForm, setOpenAddForm] = React.useState(false);
    const [openDocList, setOpenDocList] = React.useState(false);
    const [openExpensePopup, setOpenExpensePopup] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [deleteItem, setDeleteItem] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [owners, setOwners] = React.useState([]);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef(null);
    const dispatch = useDispatch();
    const [role, setRole] = React.useState({});

    React.useEffect(() => {
        fetchData();
        fetchOwners();
        fetchRole();
    }, [page, rowsPerPage, searchQuery]);

    const fetchRole = async () => {
        const res = await getDefaultProperty(dispatch);
        if (res?.data?.role && res?.data?.role?.permission) {
            setRole(JSON.parse(res?.data?.role?.permission));
        }
    };

    const fetchData = async () => {
        try {
            const res = await getAllProperties(rowsPerPage, page * rowsPerPage, searchQuery);
            setData(res?.property);
            setTotalData(res?.totalCount);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data. Please try again.");
        }
    };

    const fetchOwners = async () => {
        try {
            const res = await getAllOwners(100000, 0);
            setOwners(res?.owners);
        } catch (error) {
            console.error("Error fetching owners:", error);
            toast.error("Failed to fetch owners. Please try again.");
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(+event.target.value); setPage(0); };
    const handleAddProperty = () => { setSelectedRow(null); setOpenAddForm(true); };
    const handleCloseAddForm = () => setOpenAddForm(false);
    const handleSubmitAddProperty = async () => { setOpenAddForm(false); setIsLoading(true); fetchData(); };
    const handleEditClick = (row) => { setSelectedRow(row); setOpenAddForm(true); };
    const handleExpenseClick = (row) => { setSelectedRow(row); setOpenExpensePopup(true); };
    const handleCloseExpense = () => setOpenExpensePopup(false);
    const handleDocClick = (row) => { setSelectedRow(row); setOpenDocList(true); };
    const handleCloseDocList = () => setOpenDocList(false);
    const handleDeleteClick = (row) => { setDeleteItem(row?.id); setDeleteConfirmationOpen(true); };
    const handleSearchChange = (event) => setSearchQuery(event.target.value);
    const handleSearch = () => fetchData();
    const focusInput = () => inputRef.current.focus();

    const handleDeleteConfirmation = async () => {
        try {
            await deleteProperty(deleteItem);
            toast.success("Property deleted successfully.");
            fetchData();
            setDeleteConfirmationOpen(false);
        } catch (error) {
            console.error("Error deleting property:", error);
            toast.error("Failed to delete property. Please try again.");
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ p: "24px 28px" }}>
                <Header title="Properties" />
                <Box sx={{ mt: 4, textAlign: "center", color: "#5a6a85" }}>Loading...</Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: "24px 28px" }}>
            <Header title="Properties" subtitle="List of Properties" />
            <Paper sx={{ width: "100%", overflow: "hidden", mt: 2.5, borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <Box display="flex" alignItems="center" gap={1.5} p={2}>
                    <Button
                        href={process.env.REACT_APP_BASE_URL + "properties/download-csv"}
                        target="_blank"
                        variant="outlined"
                        size="small"
                        sx={{ mr: "auto", borderColor: "#c1c9d6", color: "#5a6a85", "&:hover": { borderColor: "#a9b4b9" } }}
                    >
                        CSV <GetAppIcon sx={{ ml: 0.5, fontSize: 18 }} />
                    </Button>
                    <InputBase
                        placeholder="Search..."
                        sx={{ backgroundColor: "#eef1f6", borderRadius: "8px", px: 1.5, py: 0.5, fontSize: "0.8125rem", width: 200 }}
                        inputRef={inputRef}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={focusInput}
                    />
                    <IconButton onClick={handleSearch} size="small">
                        <SearchIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    {role?.property?.add && (
                        <Button variant="contained" color="primary" size="small" onClick={handleAddProperty}>
                            Add Property
                        </Button>
                    )}
                </Box>
                <TableContainer sx={{ maxHeight: 900 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Property Name</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Phone Number</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Image</TableCell>
                                <TableCell>Documents</TableCell>
                                <TableCell>Expense</TableCell>
                                {(role?.property?.delete || role?.property?.update) && (
                                    <TableCell>Actions</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow hover key={row.id} sx={row.isSold ? { backgroundColor: "#fff8f8" } : {}}>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {row.propertyName}
                                        {row.isSold && (
                                            <Chip label="SOLD" size="small" sx={{ ml: 1, height: 20, fontSize: "0.625rem", fontWeight: 700, backgroundColor: "#ffebee", color: "#d32f2f" }} />
                                        )}
                                    </TableCell>
                                    <TableCell>{row.owner.name}</TableCell>
                                    <TableCell>{row.phone}</TableCell>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>{row.address}</TableCell>
                                    <TableCell>
                                        {row?.image ? (
                                            <img src={row.image} alt="Property" width={60} height={60} style={{ borderRadius: 6, objectFit: "cover" }} />
                                        ) : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleDocClick(row)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleExpenseClick(row)}>
                                            <GiWallet size={16} />
                                        </IconButton>
                                    </TableCell>
                                    {(role?.property?.delete || role?.property?.update) && (
                                        <TableCell>
                                            {role?.property?.update && !row?.isSold && (
                                                <IconButton size="small" onClick={() => handleEditClick(row)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {role?.property?.delete && (
                                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalData}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <Add open={openAddForm} onClose={handleCloseAddForm} onSubmit={handleSubmitAddProperty} editData={selectedRow} editMode={!!selectedRow} owners={owners} />
                <TitlebarImageList open={openDocList} onClose={handleCloseDocList} editData={selectedRow} editMode={!!selectedRow} />
                <PropertyExpense open={openExpensePopup} onClose={handleCloseExpense} editData={selectedRow} editMode={!!selectedRow} />
                <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Are you sure you want to delete?</DialogTitle>
                    <DialogActions>
                        <Button onClick={() => setDeleteConfirmationOpen(false)} sx={{ color: "#5a6a85" }}>Cancel</Button>
                        <Button onClick={handleDeleteConfirmation} color="error" autoFocus>Delete</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default Properties;
